import { useEffect, useRef } from 'react';
import { drainQueue } from './push';
import { pullAll } from './pull';
import { migrateAnonymousToUser } from './migrate';
import { metaRepo } from '../db/meta';
import { isOffline } from '../network';
import useStore from '../../store';

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const SYNC_EVENT = 'streaks-sync-now';

let syncing = false;

async function syncNow(userId, refreshLocal, markFirstSyncComplete) {
  if (syncing) {
    // A sync is already running (it will call markFirstSyncComplete in its
    // finally). Unblock the gate anyway so a racing trigger can never leave the
    // Dashboard's boot spinner / onboarding gate stuck on this no-op path.
    if (markFirstSyncComplete) markFirstSyncComplete();
    return;
  }
  if (isOffline()) {
    // Even when we can't reach the network, unblock the UI — the
    // Dashboard waits on this flag before deciding whether to send a
    // signed-in user into Onboarding. Without this, an offline cold
    // start would hang on a boot spinner forever.
    if (markFirstSyncComplete) markFirstSyncComplete();
    return;
  }
  if (!userId || typeof userId !== 'string' || userId.startsWith('anon-')) {
    if (markFirstSyncComplete) markFirstSyncComplete();
    return;
  }
  syncing = true;
  try {
    await drainQueue().catch(() => undefined);
    await pullAll(userId).catch(() => undefined);
    if (refreshLocal) {
      try { await refreshLocal(); } catch {}
    }
  } finally {
    syncing = false;
    if (markFirstSyncComplete) markFirstSyncComplete();
  }
}

/**
 * Mount once at app root. Detects anonymous→authed transition and triggers
 * migration before kicking off the first sync.
 */
export function useSyncEngine() {
  const user = useStore((s) => s.user);
  const refreshAfterSync = useStore((s) => s.refreshAfterSync);
  const setFirstSyncComplete = useStore((s) => s.setFirstSyncComplete);
  const lastUserIdRef = useRef(undefined);

  const cognitoUserId = user?.userId || user?.username || null;
  const markDone = () => setFirstSyncComplete(true);

  useEffect(() => {
    if (!cognitoUserId) {
      lastUserIdRef.current = null;
      setFirstSyncComplete(false);
      return undefined;
    }

    // New session — block UI on the first pull again.
    setFirstSyncComplete(false);

    let cancelled = false;
    (async () => {
      const previous = lastUserIdRef.current;
      lastUserIdRef.current = cognitoUserId;
      // First time we see a Cognito user this session — check for anon data
      // and migrate before pulling.
      if (previous === undefined || previous === null) {
        const anonId = await metaRepo.getAnonUserId().catch(() => undefined);
        if (anonId && anonId !== cognitoUserId) {
          await migrateAnonymousToUser({ anonUserId: anonId, newUserId: cognitoUserId }).catch(() => undefined);
        }
      }
      if (cancelled) return;
      await syncNow(cognitoUserId, refreshAfterSync, markDone);
    })();

    const onOnline = () => { void syncNow(cognitoUserId, refreshAfterSync, markDone); };
    const onSyncRequest = () => { void syncNow(cognitoUserId, refreshAfterSync, markDone); };
    window.addEventListener('online', onOnline);
    window.addEventListener(SYNC_EVENT, onSyncRequest);

    const interval = window.setInterval(() => {
      void syncNow(cognitoUserId, refreshAfterSync, markDone);
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.removeEventListener('online', onOnline);
      window.removeEventListener(SYNC_EVENT, onSyncRequest);
      window.clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cognitoUserId, refreshAfterSync]);
}

export function requestSync() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(SYNC_EVENT));
  }
}
